import { useMemo } from "react";
import "./FilePreview.css";

import { getFileConfig } from "../../../types/files";
import Icon from "../Icon";
import Tooltip from "../Tooltip";
import FileThumbnail from "./FileThumbnail";

/**
 * Props:
 *  - file: File | { name, size, type?, url? }
 *  - index?: number  // shown as (index)
 *  - onRemove?: () => void
 *  - onView?: (url) => void
 *
 * Behavior:
 *  - If `file` is a native File object and no url provided, component creates an object URL for view/download.
 *  - Renders like: (70) WhatsApp.html (505K)    [×]
 *  - No icons, minimal markup to match screenshot.
 */

interface FilePreviewProps {
  file:
    | File
    | {
        name: string;
        size?: number;
        bytes?: number;
        type?: string;
        url?: string;
      };
  index?: number;
  onRemove?: (index?: number) => void;
  onView?: (url: string | null) => void;
}

export default function FilePreview({
  file,
  index,
  onRemove,
  onView,
}: FilePreviewProps) {
  if (!file) return null;

  const name = file?.name ?? "unknown";
  const sizeBytes: number | null =
    file instanceof File
      ? file.size
      : "size" in file && file.size !== undefined
        ? file.size
        : "bytes" in file && file.bytes !== undefined
          ? file.bytes
          : null;
  // Files picked via the dropzone already carry a `.preview` blob URL from
  // useFileUpload — reuse it instead of minting a second one for the same File.
  const urlFromProp =
    ("url" in file ? file?.url : null) ??
    ("preview" in file ? (file as { preview?: string }).preview : null) ??
    null;

  // If file is a native File and no url provided, createObjectURL.
  const url = useMemo(() => {
    if (urlFromProp) return urlFromProp;
    if (typeof window !== "undefined" && file instanceof File) {
      try {
        return URL.createObjectURL(file);
      } catch (e) {
        return null;
      }
    }
    return null;
  }, [file, urlFromProp]);

  const formatSizeShort = (bytes: number | null) => {
    if (!bytes && bytes !== 0) return "";
    const kb = Math.round(bytes / 1024);
    if (kb < 1024) return `${kb}K`;
    const mb = Math.round((bytes / 1024 / 1024) * 10) / 10;
    return `${mb}M`;
  };

  const handleView = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onView) return onView(url ?? null);
    if (url) window.open(url, "_blank", "noopener");
  };

  const handleRemove = (index?: number) => {
    if (onRemove) onRemove(index);
  };

  const fileConfig = getFileConfig({ name } as File);

  return (
    <div className="fp-card" role="group" aria-label={`Attachment ${name}`}>
      <div className="fp-card-body">
        <div
          className={`fp-thumb ${url ? "fp-thumb-clickable" : ""}`}
          onClick={url ? handleView : undefined}
        >
          <FileThumbnail
            file={{
              name,
              preview: url ?? undefined,
              type: "type" in file ? file.type : undefined,
            }}
            className="fp-thumb-media"
            iconClassName="fp-thumb-icon"
          />
        </div>
        <div className="fp-footer">
          <Icon name={fileConfig?.icon} className="fp-icon" />
          <Tooltip content={name} position="bottom center">
            <span className="fp-name">{name}</span>
          </Tooltip>
          {sizeBytes != null && (
            <span className="fp-size">
              &nbsp;({formatSizeShort(sizeBytes)})
            </span>
          )}
        </div>
      </div>
      {onRemove ? (
        <div
          className="fp-remove"
          role="button"
          aria-label={`Remove ${name}`}
          onClick={() => handleRemove(index)}
        >
          <Icon name="Close" className="fp-remove-icon" />
        </div>
      ) : null}
    </div>
  );
}
